import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ClipboardCheck, ChevronDown, ChevronUp, Send, Loader2, FileSearch } from "lucide-react";
import DocumentPreviewContent from "@/components/DocumentPreviewContent";
import ZoomablePreview from "@/components/ZoomablePreview";

const ENTITY_LABELS = { bank_account: "Rekening Bank", address: "Alamat Perusahaan", signature: "TTD/Cap/Materai", branch: "Cabang", logo: "Logo Perusahaan", user: "User", document: "Dokumen (SPH/Invoice)", report_schedule: "Jadwal Laporan Terjadwal" };
const DOC_TYPE_LABELS = { invoice: "Invoice", quotation: "SPH", kontrak_design: "Kontrak Design", kontrak_produksi: "Kontrak Produksi", dokumen_lain: "Dokumen Bebas" };
const ACTION_LABELS = { create: "Tambah", update: "Ubah", delete: "Hapus" };
const STATUS_BADGE = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-green-500/15 text-green-400 border-green-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};
const STATUS_LABEL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak" };

const fieldDiff = (data) => {
  if (!data) return null;
  const { password_hash, ...rest } = data;
  return rest;
};
const docSummary = (data) => {
  if (!data) return null;
  return {
    Tipe: DOC_TYPE_LABELS[data.type] || data.title || "-",
    Nomor: data.number || "-",
    Total: typeof data.total === "number" ? `Rp ${data.total.toLocaleString("id-ID")}` : "-",
    "Jumlah Item": Array.isArray(data.items) ? data.items.length : "-",
  };
};

function ChangeCard({ c, canAct, onApprove, onReject, onComment, expanded, onToggle, currentUserId, quotationTemplate }) {
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isDoc = c.entity_type === "document";
  const oldShown = isDoc ? docSummary(c.old_data) : fieldDiff(c.old_data);
  const newShown = isDoc ? docSummary(c.new_data) : fieldDiff(c.new_data);
  const comments = c.comments || [];
  // old_data on a document change is only a partial snapshot (type/number/total) for
  // display comparison — new_data is the full computed document (create/update), which
  // is what can actually be rendered as a real preview. Nothing to preview on a delete.
  const canPreview = isDoc && c.new_data && Array.isArray(c.new_data.items);

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setPosting(true);
    try { await onComment(c.change_id, commentText.trim()); setCommentText(""); }
    finally { setPosting(false); }
  };

  return (
    <div className="card-surface rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-white/5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {ACTION_LABELS[c.action] || c.action} {ENTITY_LABELS[c.entity_type] || c.entity_type}
              {isDoc && c.lead_nama && <span className="text-zinc-400 font-normal"> — client {c.lead_nama}</span>}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_BADGE[c.status] || ""}`}>{STATUS_LABEL[c.status] || c.status}</span>
            {comments.length > 0 && <span className="text-[10px] text-zinc-500">💬 {comments.length}</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">Diajukan oleh {c.requested_by_name} · {new Date(c.created_at).toLocaleString("id-ID")}</div>
          {c.status !== "pending" && c.reviewed_by_name && (
            <div className="text-xs text-zinc-500 mt-0.5">
              {c.status === "approved" ? "Disetujui" : "Ditolak"} oleh {c.reviewed_by_name} · {c.reviewed_at ? new Date(c.reviewed_at).toLocaleString("id-ID") : ""}
              {c.reject_reason ? ` — "${c.reject_reason}"` : ""}
            </div>
          )}
        </div>
        {expanded ? <ChevronUp size={16} className="shrink-0 text-zinc-500 mt-1" /> : <ChevronDown size={16} className="shrink-0 text-zinc-500 mt-1" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-white/5 pt-3">
          <div className="grid grid-cols-2 gap-3 text-xs mb-3">
            <div className="bg-black/30 rounded-lg p-2.5">
              <div className="text-zinc-500 uppercase text-[10px] mb-1">Data Lama</div>
              {oldShown ? Object.entries(oldShown).map(([k, v]) => (
                <div key={k} className="text-zinc-400"><span className="text-zinc-600">{k}:</span> {String(v ?? "-")}</div>
              )) : <div className="text-zinc-600 italic">— (baru) —</div>}
            </div>
            <div className="bg-black/30 rounded-lg p-2.5">
              <div className="text-zinc-500 uppercase text-[10px] mb-1">Data Baru</div>
              {newShown ? Object.entries(newShown).map(([k, v]) => (
                <div key={k} className="text-zinc-300"><span className="text-zinc-600">{k}:</span> {String(v ?? "-")}</div>
              )) : <div className="text-zinc-600 italic">— (dihapus) —</div>}
            </div>
          </div>

          <div className="flex gap-2 mb-3 flex-wrap">
            {canPreview && (
              <Button onClick={() => setPreviewOpen(true)} variant="outline" className="h-8 bg-[#18181B] border-white/10 text-xs px-3 text-blue-300">
                <FileSearch size={14} className="mr-1.5" />Lihat Preview Dokumen
              </Button>
            )}
            {canAct && c.status === "pending" && (
              <>
                <Button onClick={() => onApprove(c.change_id)} className="h-8 bg-green-700 hover:bg-green-800 text-xs px-3">Setujui</Button>
                <Button onClick={() => onReject(c.change_id)} variant="outline" className="h-8 bg-[#18181B] border-white/10 text-xs px-3 text-red-400">Tolak</Button>
              </>
            )}
          </div>

          {canPreview && (
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent className="bg-zinc-200 border-white/10 max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-auto p-0">
                <DialogHeader className="px-4 pt-4"><DialogTitle className="font-heading text-zinc-900">Preview Dokumen — {docSummary(c.new_data)?.Tipe} {c.new_data?.number || ""}</DialogTitle></DialogHeader>
                <p className="px-4 text-[11px] text-zinc-500 -mt-1">Cubit untuk zoom, geser untuk lihat bagian lain, ketuk 2x untuk zoom cepat.</p>
                <ZoomablePreview className="p-3 min-w-[820px]">
                  {quotationTemplate ? <DocumentPreviewContent d={c.new_data} tpl={quotationTemplate} /> : null}
                </ZoomablePreview>
              </DialogContent>
            </Dialog>
          )}

          <div className="space-y-2 mb-2">
            {comments.map((cm) => (
              <div key={cm.comment_id} className={`text-xs rounded-lg p-2.5 ${cm.user_id === currentUserId ? "bg-blue-500/10 ml-6" : "bg-white/5 mr-6"}`}>
                <div className="text-zinc-400 mb-0.5"><span className="font-medium text-zinc-300">{cm.name}</span> <span className="text-zinc-600 capitalize">({cm.role})</span> · {new Date(cm.created_at).toLocaleString("id-ID")}</div>
                <div className="text-zinc-200">{cm.text}</div>
              </div>
            ))}
            {comments.length === 0 && <p className="text-xs text-zinc-600 italic">Belum ada komentar / follow-up.</p>}
          </div>

          <div className="flex gap-2">
            <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={1}
              placeholder="Tulis follow-up / komentar…" className="flex-1 bg-[#18181B] border border-white/10 rounded-lg px-2.5 py-2 text-xs resize-none" />
            <Button onClick={submitComment} disabled={posting || !commentText.trim()} className="h-auto bg-blue-600 hover:bg-blue-700 px-3">
              {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Persetujuan() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { quotationTemplate } = useMeta();
  const canReview = ["owner", "chief", "manager"].includes(user?.role);
  const [reviewTab, setReviewTab] = useState("pending"); // pending | all (for reviewers)
  const [mineFilter, setMineFilter] = useState("all"); // all | pending | approved | rejected (for admin)
  const [expandedId, setExpandedId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const reviewQuery = useQuery({
    queryKey: ["pending-changes", reviewTab],
    queryFn: async () => (await api.get(`/pending-changes?status=${reviewTab}`)).data,
    enabled: canReview,
    refetchInterval: 30000,
  });
  const mineQuery = useQuery({
    queryKey: ["pending-changes-mine"],
    queryFn: async () => (await api.get("/pending-changes/mine")).data,
    enabled: !canReview,
    refetchInterval: 30000,
  });

  const items = canReview ? (reviewQuery.data || []) : (mineQuery.data || []).filter((c) => mineFilter === "all" || c.status === mineFilter);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["pending-changes"] }); qc.invalidateQueries({ queryKey: ["pending-changes-mine"] }); qc.invalidateQueries({ queryKey: ["notifications"] }); };

  const approveChange = async (id) => {
    try { await api.post(`/pending-changes/${id}/approve`); toast.success("Perubahan disetujui & diterapkan"); invalidate(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const rejectChange = async () => {
    try {
      await api.post(`/pending-changes/${rejectTarget}/reject`, { reason: rejectReason });
      toast.success("Perubahan ditolak"); setRejectTarget(null); setRejectReason(""); invalidate();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const postComment = async (id, text) => {
    try { await api.post(`/pending-changes/${id}/comments`, { text }); invalidate(); }
    catch (e) { toast.error(apiErr(e)); throw e; }
  };

  const loading = canReview ? reviewQuery.isLoading : mineQuery.isLoading;

  return (
    <div className="px-4 py-5 max-w-3xl mx-auto md:max-w-none md:px-8">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="text-blue-400" size={22} />
        <h1 className="text-xl font-bold font-heading tracking-tight">Persetujuan</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        {canReview
          ? "Perubahan sensitif dan dokumen SPH/Invoice yang perlu Anda review, setujui, atau tolak."
          : "Riwayat pengajuan Anda — pantau status dan follow-up langsung kalau belum direspons."}
      </p>

      {canReview ? (
        <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5 mb-4 w-fit">
          <button onClick={() => setReviewTab("pending")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${reviewTab === "pending" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Menunggu</button>
          <button onClick={() => setReviewTab("all")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${reviewTab === "all" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Riwayat</button>
        </div>
      ) : (
        <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5 mb-4 w-fit overflow-x-auto hide-scrollbar">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button key={f} onClick={() => setMineFilter(f)} className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${mineFilter === f ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>
              {f === "all" ? "Semua" : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
      )}

      {loading ? null : (
        <div className="space-y-2">
          {items.map((c) => (
            <ChangeCard key={c.change_id} c={c} canAct={canReview} currentUserId={user?.user_id} quotationTemplate={quotationTemplate}
              expanded={expandedId === c.change_id} onToggle={() => setExpandedId(expandedId === c.change_id ? null : c.change_id)}
              onApprove={approveChange} onReject={(id) => { setRejectTarget(id); setExpandedId(id); }} onComment={postComment} />
          ))}
          {!items.length && <p className="text-sm text-zinc-500 text-center py-8">{canReview ? "Tidak ada perubahan di sini." : "Belum ada pengajuan."}</p>}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="bg-[#0f0f12] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">Tolak Perubahan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-zinc-400 uppercase">Alasan (opsional)</Label>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="mis. Data tidak sesuai dokumen resmi" className="w-full bg-[#18181B] border border-white/10 rounded-lg p-2.5 text-sm" />
            <Button onClick={rejectChange} className="w-full bg-red-700 hover:bg-red-800">Tolak Perubahan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
